// Constants
export const MAX_AGE = 400 * 24 * 60 * 60; // 400 days in seconds (max allowed by browsers)

/**
 * Cookie options interface
 */
interface CookieOptions {
  maxAge?: number | null;
  path?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

/**
 * Get a cookie value by name
 * @param {string} name - The name of the cookie
 * @returns {string|null} The cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  const value = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`))
    ?.split('=')[1];
  return value ? decodeURIComponent(value) : null;
};

/**
 * Set a cookie with the given name and value
 * @param {string} name - The name of the cookie
 * @param {string} value - The value to store
 * @param {CookieOptions} options - Cookie options
 * @param {number|null} [options.maxAge] - Max age in seconds, null for session cookie, defaults to 400 days
 * @param {string} [options.path] - Cookie path
 * @param {boolean} [options.secure] - Secure flag
 * @param {string} [options.sameSite] - SameSite attribute ('Strict', 'Lax', or 'None')
 */
export const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  const {
    maxAge = MAX_AGE,
    path = '/',
    secure = true,
    sameSite = 'Strict'
  } = options;

  let cookieString = `${name}=${encodeURIComponent(value)}`;
  
  if (path) cookieString += `; path=${path}`;
  if (maxAge !== null) cookieString += `; max-age=${maxAge}`;
  if (secure) cookieString += '; Secure';
  if (sameSite) cookieString += `; SameSite=${sameSite}`;

  document.cookie = cookieString;
};

/**
 * Remove a cookie by name
 * @param {string} name - The name of the cookie to remove
 * @param {string} [path='/'] - The path of the cookie
 */
export const removeCookie = (name: string, path: string = '/'): void => {
  document.cookie = `${name}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
};

/**
 * Check if a cookie exists
 * @param {string} name - The name of the cookie
 * @returns {boolean} True if the cookie exists
 */
export const hasCookie = (name: string): boolean => {
  return getCookie(name) !== null;
}; 